import { formatNumber } from '@/app/common/measures/format.ts';
import {
  ONE_FT_IN_KM,
  ONE_FT_IN_M,
  ONE_LB_IN_G,
  ONE_LB_IN_KG,
  ONE_MILE_IN_KM,
  ONE_MILE_IN_M,
} from '@/app/common/measures/constants.ts';
import type { PartialLocalization, UNITS } from '@/app/common/measures/types.ts';
import { DISTANCE_UNITS, WEIGHT_UNITS } from '@/app/common/measures/types.ts';
import { applyDecimals, getDecimals } from '@/app/common/measures/decimals.ts';
import { getSuffix } from '@/app/common/measures/localizations.ts';
import { applyRounding, getRoundingFunction } from '@/app/common/measures/rounding.ts';

// Kilometers
export const kmToMeters = (value: number) => value * 1000;
export const kmToMiles = (value: number) => value / ONE_MILE_IN_KM;
export const kmToFeet = (value: number) => value / ONE_FT_IN_KM;

// Meters
export const mToKm = (value: number) => value / 1000;
export const mToMiles = (value: number) => value / ONE_MILE_IN_M;
export const mToFeet = (value: number) => value / ONE_FT_IN_M;

// weights
export const kgToGrams = (value: number) => value * 1000;
export const kgToLbs = (value: number) => value / ONE_LB_IN_KG;
export const gToKg = (value: number) => value / 1000;
export const gToLbs = (value: number) => value / ONE_LB_IN_G;

// Pure conversion function that only handles unit conversion
export const convertValue = (value: number, initialUnit: UNITS, finalUnit: UNITS): number => {
  if (initialUnit === finalUnit) return value;

  switch (initialUnit) {
    case DISTANCE_UNITS.KM:
      if (finalUnit === DISTANCE_UNITS.M) return kmToMeters(value);
      if (finalUnit === DISTANCE_UNITS.MI) return kmToMiles(value);
      if (finalUnit === DISTANCE_UNITS.FT) return kmToFeet(value);
      break;
    case DISTANCE_UNITS.M:
      if (finalUnit === DISTANCE_UNITS.KM) return mToKm(value);
      if (finalUnit === DISTANCE_UNITS.MI) return mToMiles(value);
      if (finalUnit === DISTANCE_UNITS.FT) return mToFeet(value);
      break;
    case WEIGHT_UNITS.KG:
      if (finalUnit === WEIGHT_UNITS.LBS) return kgToLbs(value);
      if (finalUnit === WEIGHT_UNITS.G) return kgToGrams(value);
      break;
    case WEIGHT_UNITS.G:
      if (finalUnit === WEIGHT_UNITS.KG) return gToKg(value);
      if (finalUnit === WEIGHT_UNITS.LBS) return gToLbs(value);
      break;
    default:
      return value;
  }

  return value;
};

const getNewConvertedValue = (value: number, from: UNITS, to: UNITS, decimals: number): number => {
  const converted = convertValue(value, from, to);
  const withDecimals = applyDecimals(converted, decimals);
  const withRounding = applyRounding(withDecimals, getRoundingFunction(to));

  return withRounding;
};

export const convertToDifferentUnit = (
  value: number,
  from: UNITS,
  to: UNITS,
  localization: PartialLocalization,
  overrideDecimals?: number
): { formatted?: string; value: number; unit: string } => {
  const { lang, locale } = localization;
  const decimals = getDecimals(to, overrideDecimals);
  const newValue = getNewConvertedValue(value, from, to, decimals);
  const suffix = getSuffix(to, lang);
  const formattedValue = formatNumber(newValue, locale, suffix);

  return {
    formatted: formattedValue,
    value: newValue,
    unit: suffix,
  };
};
